import type { WorkspaceViewerNode } from '@/lib/workspace-viewer-types';

export type WorkspaceOpenTabSearchItem = {
  id: string;
  title: string;
  path: string;
  text?: string | null;
};

export type WorkspaceOpenTabMatchKind = 'none' | 'title' | 'path' | 'content';

export type WorkspaceOpenTabSearchResult<
  TItem extends WorkspaceOpenTabSearchItem = WorkspaceOpenTabSearchItem,
> = {
  item: TItem;
  matchKind: WorkspaceOpenTabMatchKind;
  snippet: string | null;
};

const BINARY_VIEWER_KINDS = new Set(['3d', 'audio', 'image', 'pdf', 'video']);

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function contentSnippet(text: string, query: string): string | null {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (!compact) return null;

  const match = new RegExp(escapeRegExp(query), 'iu').exec(compact);
  if (!match || match.index === undefined) return null;

  const start = Math.max(0, match.index - 44);
  const end = Math.min(compact.length, match.index + match[0].length + 76);
  return `${start > 0 ? '…' : ''}${compact.slice(start, end)}${
    end < compact.length ? '…' : ''
  }`;
}

function matchScore(value: string, query: string, base: number): number {
  const candidate = normalized(value);
  if (candidate === query) return base + 90;
  if (candidate.startsWith(query)) return base + 60;
  if (candidate.includes(query)) return base + 30;
  return 0;
}

/**
 * Ranks the bounded set of open workspace tabs without scanning the workspace.
 * Title matches win over path matches, which win over authorized text content.
 */
export function searchWorkspaceOpenTabs<
  TItem extends WorkspaceOpenTabSearchItem,
>(
  items: readonly TItem[],
  rawQuery: string,
  limit = 12
): WorkspaceOpenTabSearchResult<TItem>[] {
  const query = normalized(rawQuery);
  const boundedLimit = Math.max(0, Math.floor(limit));
  if (boundedLimit === 0) return [];

  if (!query) {
    return items.slice(0, boundedLimit).map((item) => ({
      item,
      matchKind: 'none',
      snippet: null,
    }));
  }

  return items
    .map((item, index) => {
      const titleScore = matchScore(item.title, query, 300);
      const pathScore = matchScore(item.path, query, 200);
      const snippet = item.text ? contentSnippet(item.text, query) : null;
      const contentScore = snippet ? 100 : 0;
      const score = Math.max(titleScore, pathScore, contentScore);
      const matchKind: WorkspaceOpenTabMatchKind = titleScore
        ? 'title'
        : pathScore
          ? 'path'
          : contentScore
            ? 'content'
            : 'none';
      return { item, index, matchKind, score, snippet };
    })
    .filter((result) => result.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, boundedLimit)
    .map(({ item, matchKind, snippet }) => ({ item, matchKind, snippet }));
}

export function searchableWorkspaceOpenTabText(
  node: WorkspaceViewerNode
): string | null {
  if (node.type !== 'file' || !node.text) return null;
  if (BINARY_VIEWER_KINDS.has(node.kind.toLocaleLowerCase())) return null;
  return node.text;
}

/**
 * Resolves only the exact paths already present in the tab strip. Individual
 * failures stay indistinguishable and simply make that tab title-only.
 */
export async function loadWorkspaceOpenTabText(
  paths: readonly string[],
  resolveFile: (path: string) => Promise<WorkspaceViewerNode>
): Promise<ReadonlyMap<string, string | null>> {
  const uniquePaths = Array.from(
    new Set(paths.map((path) => path.trim()))
  ).filter(Boolean);
  const resolved = await Promise.all(
    uniquePaths.map(async (path) => {
      try {
        const node = await resolveFile(path);
        return { path, text: searchableWorkspaceOpenTabText(node) };
      } catch {
        return { path, text: null };
      }
    })
  );

  const textByPath = new Map<string, string | null>();
  for (const { path, text } of resolved) {
    textByPath.set(path, text);
  }
  return textByPath;
}
